import { usePoolDetailsQuery } from 'graphql/__generated__/types-and-hooks'
import { getPercentChange } from 'utils/getPercentChange'
import nFormatter from 'utils/nFormatter'

export interface PoolDetails {
  token0: {
    id: string
    decimals: number
    symbol: string
  }
  token1: {
    id: string
    decimals: number
    symbol: string
  }
  apr: string
  fees: {
    day: string
  }
  volume: {
    value: string
    change: number
  }
  tvl: {
    value: string
    change: number
  }
  token0Price: string
  token1Price: string
  poolFees: number
  address: string
  totalValueLockedToken0: string
  totalValueLockedToken1: string
}

export default function usePoolDetails(address?: string): {
  isLoading: boolean
  data: PoolDetails | undefined
} {
  const { data, loading } = usePoolDetailsQuery({
    variables: { address: address?.toLocaleLowerCase() ?? '' },
    skip: !address,
  })

  if (loading) return { isLoading: true, data: undefined }
  if (!data || !data.pool) return { isLoading: false, data: undefined }

  const {
    token0,
    token1,
    poolDayData,
    token0Price,
    token1Price,
    totalValueLockedToken0,
    totalValueLockedToken1,
    feeTier,
  } = data.pool

  const token0PriceUsd = token0.tokenDayData[0]?.priceUSD ?? 0
  const token1PriceUsd = token1.tokenDayData[0]?.priceUSD ?? 0
  const feesUSD = poolDayData[1]?.feesUSD ?? 0
  const liquidity = totalValueLockedToken0 * token0PriceUsd + totalValueLockedToken1 * token1PriceUsd

  const result = {
    token0: {
      id: token0.id,
      symbol: token0.symbol,
      decimals: token0.decimals,
    },
    token1: {
      id: token1.id,
      symbol: token1.symbol,
      decimals: token1.decimals,
    },
    apr: nFormatter(liquidity === 0 || feesUSD === 0 ? 0 : (feesUSD * 365 * 100) / liquidity),
    fees: {
      day: poolDayData.reduce((acc, cur) => acc + +cur.feesUSD, 0).toFixed(2),
    },
    volume: {
      value: nFormatter(Number(poolDayData[0]?.volumeUSD ?? 0)),
      change: +nFormatter(getPercentChange(poolDayData[0]?.volumeUSD, poolDayData[1]?.volumeUSD)),
    },
    poolFees: +nFormatter(feeTier / 10000),
    tvl: {
      value: nFormatter(liquidity),
      change: +nFormatter(getPercentChange(poolDayData[0]?.tvlUSD, poolDayData[1]?.tvlUSD)),
    },
    token0Price,
    token1Price,
    address,
    totalValueLockedToken0: nFormatter(+data.pool.totalValueLockedToken0),
    totalValueLockedToken1: nFormatter(+data.pool.totalValueLockedToken1),
  } as PoolDetails

  return { isLoading: loading, data: result }
}
